import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "./Reveal";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function Services() {
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  if (!services.length) return null;

  return (
    <section id="services" data-testid="services-section" className="bg-heirloom-cream py-24 md:py-28">
      <div className="max-w-[1300px] mx-auto px-6">
        <Reveal className="text-center mb-14">
          <span className="gold-rule overline text-heirloom-maroon">What We Do</span>
          <h2 className="font-display text-4xl md:text-5xl text-heirloom-obsidian mt-4">
            Our <span className="text-heirloom-maroon italic">Signature</span> Services
          </h2>
          <p className="text-heirloom-obsidian/60 max-w-2xl mx-auto mt-4">
            A complete atelier of celebration — from full-service wedding planning to catering, decor and entertainment.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((s, i) => (
            <motion.article
              key={s.id}
              data-testid={`service-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              className="svc-card group bg-heirloom-bone rounded-2xl overflow-hidden border border-heirloom-line hover:shadow-[0_25px_60px_-30px_rgba(0,0,0,0.4)] transition-shadow duration-500 flex flex-col"
            >
              <div className="relative h-60 overflow-hidden">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-heirloom-obsidian/50 to-transparent" />
                <span className="absolute top-4 left-4 ribbon overline text-[0.6rem] px-3 py-1.5 rounded-full">{s.tagline}</span>
              </div>
              <div className="p-7 flex flex-col flex-1">
                <span className="overline text-[0.6rem] text-heirloom-gold">Event Management</span>
                <h3 className="font-display text-2xl text-heirloom-obsidian mt-2 group-hover:text-heirloom-maroon transition-colors">{s.title}</h3>
                <p className="text-sm text-heirloom-obsidian/60 mt-3 leading-relaxed flex-1">{s.description}</p>
                <button
                  data-testid={`service-readmore-${i}`}
                  onClick={() => setSelected(s)}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-heirloom-maroon hover:gap-3 transition-all duration-300 self-start"
                >
                  Read More <ArrowRight size={16} />
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden border-heirloom-line bg-heirloom-bone gap-0" data-testid="service-dialog">
          {selected && (
            <div className="grid grid-cols-1 md:grid-cols-2">
              <DialogTitle className="sr-only">{selected.title}</DialogTitle>
              <DialogDescription className="sr-only">{selected.description}</DialogDescription>
              <div className="h-56 md:h-auto min-h-[220px] relative">
                <img src={selected.image} alt={selected.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-8">
                <span className="overline text-[0.6rem] text-heirloom-gold">{selected.tagline}</span>
                <h3 className="font-display text-3xl text-heirloom-maroon mt-2">{selected.title}</h3>
                <p className="text-sm text-heirloom-obsidian/70 mt-4 leading-relaxed">{selected.description}</p>
                <div className="mt-5 space-y-2">
                  {selected.features?.map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm text-heirloom-obsidian/80">
                      <Check size={15} className="text-heirloom-maroon" /> {f}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { setSelected(null); document.querySelector("#inquiry")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="mt-7 bg-heirloom-maroon text-heirloom-bone text-xs font-semibold tracking-[0.16em] uppercase px-7 py-3.5 rounded-full hover:bg-heirloom-maroon-deep transition-colors"
                >
                  Enquire Now
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
